import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { sizeChart } from '../data/mockProducts';
import { 
  Ruler, 
  CheckCircle, 
  HelpCircle, 
  ArrowRight, 
  Droplets, 
  Wind, 
  ThermometerSun,
  AlertTriangle,
  Shirt,
  Sparkles
} from 'lucide-react';

const FitAndCare = () => {
  const { getCartItemCount } = useCart();
  const [selectedSize, setSelectedSize] = useState(null);
  const [unit, setUnit] = useState('cm');

  const headers = sizeChart.measurements.headers[unit];
  const rows = sizeChart.measurements.rows[unit];

  const careInstructions = [
    {
      icon: Droplets,
      title: 'Washing',
      instructions: [
        'Machine wash cold (30°C max)',
        'Use mild detergent only',
        'We recommend washing the suit inside out',
        'Wash in the Vesta Wash Bag for best results',
        'Secure all Velcro before washing',
        'Close all zippers before washing'
      ]
    },
    {
      icon: Wind,
      title: 'Drying',
      instructions: [
        'Hang dry only - do not tumble dry',
        'Dry away from direct sunlight',
        'Hang on a wide hanger to maintain shape',
        'Allow to fully dry before storing'
      ]
    },
    {
      icon: ThermometerSun,
      title: 'Ironing & Heat',
      instructions: [
        'Do not iron',
        'Do not dry clean',
        'Keep away from high heat sources',
        'Do not bleach'
      ]
    },
    {
      icon: Shirt,
      title: 'Storage',
      instructions: [
        'Store hanging in a cool, dry place',
        'Keep away from sharp objects',
        'Use the Vesta Wash Bag for added protection during washing'
      ]
    }
  ];

  const proTips = [
    'For best results, wash your suit weekly or when visibly soiled. We recommend keeping 2 suits on hand and washing them in rotation.',
    'Our Wash Bag helps protect your suit during machine washing.',
    'Secure Velcro closures before washing to avoid catching on fabric.'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="fit-and-care-page">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Fit & <span className="text-[#F39C49]">Care Guide</span>
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              Everything you need to know about sizing and caring for your Vesta suit.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16">
        
        {/* Size Chart Section */}
        <section className="mb-20" id="sizing">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
            <h2 className="text-4xl font-black text-[#004D51]">Size Chart</h2>
            
            {/* Unit Toggle */}
            <div className="flex items-center bg-[#F3CEB2]/30 rounded-lg p-1">
              <button
                onClick={() => setUnit('cm')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  unit === 'cm'
                    ? 'bg-[#004D51] text-white'
                    : 'text-[#545759] hover:text-[#004D51]'
                }`}
                data-testid="unit-cm"
              >
                CM
              </button>
              <button
                onClick={() => setUnit('inches')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  unit === 'inches'
                    ? 'bg-[#004D51] text-white'
                    : 'text-[#545759] hover:text-[#004D51]'
                }`}
                data-testid="unit-inches"
              >
                Inches
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#004D51] text-white">
                  {headers.map((header, index) => (
                    <th 
                      key={index} 
                      className="px-6 py-4 text-left font-black text-lg border-r border-[#003338] last:border-r-0"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    onClick={() => setSelectedSize(row[0])}
                    className={`cursor-pointer transition-all ${
                      selectedSize === row[0] 
                        ? 'bg-[#F39C49] text-white' 
                        : rowIndex % 2 === 0 
                          ? 'bg-[#F3CEB2]/20 hover:bg-[#F3CEB2]/40' 
                          : 'bg-white hover:bg-[#F3CEB2]/20'
                    }`}
                  >
                    {row.map((cell, cellIndex) => (
                      <td 
                        key={cellIndex} 
                        className={`px-6 py-4 border-r border-gray-200 last:border-r-0 ${
                          cellIndex === 0 ? 'font-black text-lg' : 'font-medium'
                        } ${selectedSize === row[0] ? '' : 'text-[#545759]'}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedSize && (
            <div className="mt-6 p-6 bg-[#DB5A14] text-white rounded-xl text-center">
              <p className="text-xl font-black">
                Size {selectedSize} looks like a good fit!
              </p>
              <Link to="/shop">
                <Button className="mt-4 bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black">
                  Shop Size {selectedSize} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </section>

        {/* How to Measure */}
        <section className="mb-20">
          <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">How to Measure</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sizeChart.howToMeasure.map((item, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-6 text-center"
              >
                <div className="w-16 h-16 bg-[#DB5A14] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ruler className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#004D51] mb-3">{item.measurement}</h3>
                <p className="text-[#545759]">{item.instruction}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Fit Tips */}
        <section className="mb-20">
          <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">Fit Tips</h2>
          
          <div className="max-w-3xl mx-auto bg-[#F3CEB2]/20 rounded-2xl p-8">
            <ul className="space-y-4">
              {sizeChart.fitAdvice.map((advice, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-[#545759] text-lg">{advice}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Care Instructions Section */}
        <section className="mb-20" id="care">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-[#004D51] mb-4">Care Instructions</h2>
            <p className="text-xl text-[#545759] max-w-2xl mx-auto">
              Proper care will extend the life of your Vesta suit and maintain its protective properties.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {careInstructions.map((section, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-8"
              >
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-[#004D51] rounded-full flex items-center justify-center mr-4">
                    <section.icon className="w-7 h-7 text-[#F39C49]" />
                  </div>
                  <h3 className="text-2xl font-black text-[#004D51]">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.instructions.map((instruction, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#545759]">{instruction}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Warning Section */}
        <section className="mb-20">
          <div className="bg-[#DB5A14]/10 border-4 border-[#DB5A14] rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <AlertTriangle className="w-10 h-10 text-[#DB5A14] mr-4" />
              <h3 className="text-2xl font-black text-[#DB5A14]">Important: Do Not</h3>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {['Tumble dry', 'Iron', 'Dry clean', 'Bleach'].map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center border-2 border-[#DB5A14]/30">
                  <span className="text-[#545759] font-bold">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pro Tips */}
        <section className="mb-20">
          <div className="bg-[#004D51] rounded-2xl p-8 md:p-12">
            <div className="flex items-center justify-center mb-8">
              <Sparkles className="w-10 h-10 text-[#F39C49] mr-4" />
              <h3 className="text-3xl font-black text-white">Pro Tips</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {proTips.map((tip, index) => (
                <div key={index} className="flex items-start bg-white/10 rounded-xl p-6">
                  <span className="text-[#F39C49] font-black text-2xl mr-4">{index + 1}</span>
                  <p className="text-white">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Wash Bag CTA */}
        <section className="mb-20">
          <div className="bg-[#F3CEB2]/30 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0 md:mr-8 text-center md:text-left">
              <h3 className="text-3xl font-black text-[#004D51] mb-4">
                Protect Your Investment
              </h3>
              <p className="text-[#545759] text-lg max-w-md">
                Our Vesta Wash Bag is specially designed to protect your suit during machine washing.
              </p>
            </div>
            <Link to="/product/vesta-wash-bag">
              <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-6 text-lg whitespace-nowrap">
                Shop Wash Bag - $12.95
              </Button>
            </Link>
          </div>
        </section>

        {/* Still Need Help */}
        <section className="bg-[#004D51] rounded-2xl p-12 text-center text-white">
          <HelpCircle className="w-16 h-16 mx-auto mb-6 text-[#F39C49]" />
          <h2 className="text-4xl font-black mb-4">Need More Help?</h2>
          <p className="text-xl text-[#CFD2D4] mb-8 max-w-2xl mx-auto">
            Have questions about sizing or caring for your suit? We're here to help!
          </p>
          <Link to="/support">
            <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-6 text-lg">
              Contact Support
            </Button>
          </Link>
        </section>
      </div>

      <StoreFooter />
    </div>
  );
};

export default FitAndCare;
